import { useDispatch, useSelector } from 'react-redux'
import { Button, Grid, InputAdornment, makeStyles, Paper, Switch, TextField, withStyles } from '@material-ui/core'
import { ContainerTitle } from '../common/container-title'
import React, { useEffect, useState } from 'react'
import { getApiMode, updateApiMode } from '../../store/actions/settings-action'
import {
  getMongoLogger,
  getMongoLoggerEnabled,
  updateMongoLogger,
  updateMongoLoggerThreshold,
  downloadMongoLoggerCsv,
  deleteMongoLoggerCsv,
} from '../../store/actions/mongo-logger-action'
import Typography from '@material-ui/core/Typography'
import { green } from '@material-ui/core/colors'
import { ImportUsers } from './ImportUsers'
import { RunScript } from './RunScript'
import GetAppIcon from '@material-ui/icons/GetApp'
import DeleteIcon from '@material-ui/icons/Delete'

const useStyles = makeStyles(theme => ({
  contentRoot: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: 16,
    marginBottom: 16,
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
  },
  vert: {
    color: '#00e676',
  },
}))

export const GreenSwitch = withStyles({
  switchBase: {
    color: green[300],
    '&$checked': {
      color: green[500],
    },
    '&$checked + $track': {
      backgroundColor: green[500],
    },
  },
  checked: {},
  track: {},
})(Switch)

export const BoAdmin = () => {
  const classes = useStyles()
  const [localApiMode, setLocalApiMode] = useState(false)
  const apiMode = useSelector(state => state.settingsModule.apiMode)
  const mongoLogger = useSelector(state => state.mongoLoggerModule)
  const [localMongoLogger, setLocalMongoLogger] = useState(false)
  const [localThreshold, setLocalThreshold] = useState(1000)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getApiMode())
    // Chargement complet au montage (threshold, CSV, enabled)
    dispatch(getMongoLogger())
  }, [])

  // Polling toutes les 10s sur l'endpoint léger /enabled pour refléter l'état en temps réel
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getMongoLoggerEnabled())
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setLocalApiMode(apiMode)
  }, [apiMode])

  useEffect(() => {
    setLocalMongoLogger(mongoLogger.enabled)
    setLocalThreshold(mongoLogger.slowQueryThresholdMs)
  }, [mongoLogger.enabled, mongoLogger.slowQueryThresholdMs])

  let handleChangeApiMode = () => {
    dispatch(updateApiMode(!apiMode))
  }

  let handleChangeMongoLogger = () => {
    dispatch(updateMongoLogger(!mongoLogger.enabled))
  }

  let handleThresholdChange = e => {
    setLocalThreshold(e.target.value)
  }

  let handleThresholdSubmit = () => {
    const val = parseInt(localThreshold, 10)
    if (!isNaN(val) && val > 0) {
      dispatch(updateMongoLoggerThreshold(val))
    }
  }

  let handleDownloadCsv = () => {
    dispatch(downloadMongoLoggerCsv())
  }

  let handleDeleteCsv = () => {
    dispatch(deleteMongoLoggerCsv())
  }

  return (
    <main>
      <Grid container spacing={2}>
        <Grid item xs={6} />
        <Grid item xs={3} container justify="flex-end" />
      </Grid>
      <br />
      <br />
      {
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div>
              <Paper className={classes.contentRoot} elevation={1}>
                <ContainerTitle classes={classes} title="Paramètre de l'outil de modélisation" />
                <Typography component="div">
                  <Grid component="label" container alignItems="center" spacing={1}>
                    <Grid item xs={5}>
                      <Typography data-test={'Api client'} variant="h5" style={{ lineHeight: 1.8 }}>
                        Api client
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <GreenSwitch
                        className={classes.vert}
                        checked={localApiMode}
                        onChange={handleChangeApiMode}
                        name="apiModeChecked"
                      />
                    </Grid>
                  </Grid>

                  <Grid component="label" container alignItems="center" spacing={1} style={{ marginTop: 8 }}>
                    <Grid item xs={5}>
                      <Typography variant="h5" style={{ lineHeight: 1.8 }}>
                        Logs MongoDB détaillés
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <GreenSwitch
                        checked={localMongoLogger}
                        onChange={handleChangeMongoLogger}
                        name="mongoLoggerChecked"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" style={{ color: localMongoLogger ? '#00c853' : '#9e9e9e' }}>
                        {localMongoLogger ? '● Activé' : '○ Désactivé'}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container alignItems="center" spacing={1} style={{ marginTop: 8 }}>
                    <Grid item xs={5}>
                      <Typography variant="h5" style={{ lineHeight: 1.8 }}>
                        Seuil requête lente (CSV)
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        value={localThreshold}
                        onChange={handleThresholdChange}
                        inputProps={{ min: 101, step: 100 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                        }}
                        style={{ width: 130 }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleThresholdSubmit}
                        disabled={parseInt(localThreshold, 10) === mongoLogger.slowQueryThresholdMs}
                      >
                        Appliquer
                      </Button>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Valeur courante : {mongoLogger.slowQueryThresholdMs}ms
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container alignItems="center" spacing={1} style={{ marginTop: 8 }}>
                    <Grid item xs={5}>
                      <Typography variant="h5" style={{ lineHeight: 1.8 }}>
                        Fichier CSV slow-queries du jour
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<GetAppIcon />}
                        onClick={handleDownloadCsv}
                        disabled={!mongoLogger.csvFileExists}
                        title={
                          mongoLogger.csvFileExists
                            ? mongoLogger.csvFilePath
                            : "Aucun fichier CSV disponible aujourd'hui"
                        }
                      >
                        Télécharger
                      </Button>
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteCsv}
                        disabled={!mongoLogger.csvFileExists}
                        style={{
                          color: mongoLogger.csvFileExists ? '#f44336' : undefined,
                          borderColor: mongoLogger.csvFileExists ? '#f44336' : undefined,
                        }}
                        title={
                          mongoLogger.csvFileExists
                            ? 'Supprimer le fichier CSV du jour'
                            : 'Aucun fichier CSV disponible'
                        }
                      >
                        Supprimer
                      </Button>
                    </Grid>
                    {mongoLogger.csvFileExists && (
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">
                          {mongoLogger.csvFilePath}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Grid>
                    <ImportUsers />
                  </Grid>
                  <Grid>
                    <RunScript />
                  </Grid>
                </Typography>
              </Paper>
            </div>
          </Grid>
        </Grid>
      }
    </main>
  )
}
